import { MAP_MIN_ZOOM } from '@/lib/enums'
import bbox from '@turf/bbox'
import { polygon } from '@turf/helpers'
import Geographic from '@/lib/geographic'
import Camera from '@/lib/camera'
import {
  camera,
  setCamera as setCameraState,
  DEFAULT_HOME,
  destination,
  setDestination,
  setViewer3d,
  setVisibleRegion as setVisibleRegionState,
} from '@/services/map-service'
import isNumber from 'lodash/isNumber'
import { maxDistancePoints, maxDistancePointsBbox } from '@/lib/feature-utils'

// Override default animation speed if necessary
const fasterScreenSpeed = { screenSpeed: 7 }
const fasterDuration = { duration: 100 }

/**
 * Changes the current view to the destination using an easing function
 * @param {Object} map - Mapbox instance
 * @param {Camera} destCamera - Eases map view to provided camera location
 */
export const updateCamera = (map, destCamera) => {
  // reset the padding that can be set globally as part of flyTo
  // Ex: https://github.com/mapbox/mapbox-gl-js/issues/11831
  map.setPadding({ left: 0, top: 0, bottom: 0, right: 0 })

  const { location, pitch, bearing, padding } = destCamera
  const { longitude, latitude, height: zoom } = location

  // Guard against invalid coordinates - prevents NaN from being passed to map
  if (!isNumber(longitude) || !isNumber(latitude) || !isNumber(zoom)) {
    return
  }
  const { location: currentLocation } = camera()
  const hasZoomChanged = currentLocation.height !== zoom
  const hasPadding = padding?.length > 0
  const hasCenterChanged =
    currentLocation.longitude !== longitude ||
    currentLocation.latitude !== latitude
  const curve = 1.2
  const flyToOptions = {
    pitch,
    bearing,
    curve,
  }

  // Only set center and zoom if changed
  // This helps with smoothing animation
  if (hasCenterChanged) {
    flyToOptions.center = [longitude, latitude]
  }

  if (hasZoomChanged) {
    flyToOptions.zoom = zoom
  }

  if (hasPadding) {
    flyToOptions.padding = {
      top: padding[0],
      right: padding[1],
      bottom: padding[2],
      left: padding[3],
    }
  }

  if (maxDistancePoints(location)) {
    Object.assign(flyToOptions, fasterScreenSpeed)
  }

  map.flyTo(flyToOptions)
}

/**
 * Changes the current view to the destination with no easing function
 * @param {Object} map - Mapbox instance
 * @param {Camera} destCamera - Jumps map view to provided camera location
 */
export const updateCameraQuick = (map, destCamera) => {
  const { location, pitch, bearing, padding } = destCamera
  const { longitude, latitude, height: zoom } = location

  // Guard against invalid coordinates - prevents NaN from being passed to map
  if (!isNumber(longitude) || !isNumber(latitude) || !isNumber(zoom)) {
    return
  }

  const hasPadding = padding?.length > 0
  const jumpToOptions = {
    pitch,
    bearing,
    zoom,
    center: [longitude, latitude],
  }

  if (hasPadding) {
    jumpToOptions.padding = {
      top: padding[0],
      right: padding[1],
      bottom: padding[2],
      left: padding[3],
    }
  }

  map.jumpTo(jumpToOptions)
}

/**
 * Update camera state with current map view
 * @param {Object} mapViewer - MapViewer instance
 */
export const setCamera = (mapViewer) => {
  // Calculate the visible center of the map
  const zoom = mapViewer.getZoom()
  const { offsetWidth: w, offsetHeight: h } = mapViewer.getCanvas()

  // Guard against zero or invalid canvas dimensions
  // This can happen during tab switches, window minimize/restore, or layout changes
  if (!w || !h || w <= 0 || h <= 0) {
    return
  }

  const [centerX, centerY] = [w / 2, h / 2]
  const visibleCenterPoint = [centerX, centerY]
  const [vcLng, vcLat] = mapViewer.convertCtxPointToLonLat(visibleCenterPoint)
  const visibleCenter = new Geographic(vcLng, vcLat, zoom)

  const altitude = mapViewer.getCameraAltitude()
  const [lng, lat] = mapViewer.getCenter()
  const bearing = mapViewer.getBearing()
  const pitch = mapViewer.getPitch()
  const location = new Geographic(lng, lat, zoom)
  const newCamera = new Camera({
    location,
    bearing,
    pitch,
    altitude,
    visibleCenter,
  })
  const isTilted = newCamera.pitch !== 0

  setCameraState(newCamera)
  setViewer3d(isTilted)
}

/**
 * Get the initial view for mapbox
 * @param {Number} [initialPitch] - Optional pitch angle to start with
 * @returns {Camera} - Camera view
 */
export const getInitView = (initialPitch) => {
  const defaultView = new Camera({
    location: DEFAULT_HOME,
    ...(initialPitch !== undefined && { pitch: initialPitch }),
  })
  try {
    performance.mark('start_time_to_get_last_view_location')
    const lastView = getLastView()
    performance.mark('end_time_to_get_last_view_location')
    performance.measure(
      'time_to_get_last_view_location',
      'start_time_to_get_last_view_location',
      'end_time_to_get_last_view_location'
    )

    const nextView = destination()
    const camera = nextView || lastView || defaultView

    // If there is an initialPitch, set the pitch
    if (initialPitch !== undefined) {
      const { location, bearing, padding, altitude, visibleCenter } = camera
      return new Camera({
        location,
        bearing,
        pitch: initialPitch,
        padding,
        altitude,
        visibleCenter,
      })
    }

    return camera
  } catch (error) {
    return defaultView
  }
}

/**
 * Save camera view to browser storage
 * @param {Object} map - Mapbox instance
 * @param {Camera} camera - Camera view
 */
export const saveLastView = (camera) => {
  camera.saveToLocalStorage()
}

/**
 * Get camera view from browser storage
 * @returns {Camera} - Camera view
 */
export const getLastView = () => {
  return Camera.fromLocalStorage()
}

/**
 * Set map view to include bounds and padding
 * @param {Object} map - mapbox instance
 * @param {Object} bounds - extent to fit in viewer
 */
export const fitBounds_ = (map, bounds) => {
  // reset the padding that can be set as part of the easeTo function so that we don't double pad
  // Ex: https://github.com/mapbox/mapbox-gl-js/issues/9643
  map.setPadding({ left: 0, top: 0, bottom: 0, right: 0 })
  // FUTURE - padding could be passed in as a param if needed
  // padding adds space around the edge of the map edge and tries to avoid any open cards
  const boundsPadding = { top: 150, bottom: 150, left: 400, right: 150 }
  const maxZoom = 19
  const params = {
    padding: boundsPadding,
    maxZoom: maxZoom,
  }
  // If destination is too far away, speed up animation
  if (maxDistancePointsBbox(bounds)) {
    Object.assign(params, fasterDuration)
  }
  map.fitBounds(bounds, params)
}

/**
 * Set map view to include feature and padding.  You can also move into a 3d view by
 * setting the bearing and pitch params, which default to 0 with the camera in 2d
 * @param {Object} map - mapbox instance
 * @param {Object} feature - geojson feature
 * @param {Number} bearing - bearing to animate the camera to, in degrees
 * @param {Number} pitch - pitch to animate the camera to
 * @param {Number} maxZoom -  maxiumum zoom level to allow in camera options
 * @param {Number} minZoom - minimum zoom level to allow in camera options
 * @param {Object} padding - amount of padding in pixels to add to the given feature bounds
 */
export const fitFeature_ = (
  map,
  feature,
  bearing = 0,
  pitch = 0,
  maxZoom = 19,
  minZoom = MAP_MIN_ZOOM,
  padding = { top: 250, bottom: 250, left: 250, right: 250 }
) => {
  const extent = bbox(feature)
  const params = { padding, bearing, maxZoom }
  const cameraForFeature = map.cameraForBounds(extent, params)

  if (cameraForFeature?.center?.lng && cameraForFeature?.center?.lat) {
    let cameraHeight = cameraForFeature.zoom
    if (minZoom && minZoom >= MAP_MIN_ZOOM) {
      cameraHeight = Math.max(minZoom, cameraHeight)
    }
    const camera = new Camera({
      location: new Geographic(
        cameraForFeature.center.lng,
        cameraForFeature.center.lat,
        cameraHeight
      ),
      bearing,
      pitch,
      padding: [padding.top, padding.right, padding.bottom, padding.left],
    })
    setDestination(camera)
  }
}

/**
 * Update the visible region state with camera update
 * @param {Object} mapViewer - a MapViewer instance
 */
export const setVisibleRegion = (mapViewer) => {
  try {
    const canvas = mapViewer.getCanvas()
    const w = canvas.offsetWidth
    const h = canvas.offsetHeight
    const cUL = mapViewer.convertCtxPointToLonLat([0, 0])
    const cUR = mapViewer.convertCtxPointToLonLat([w, 0])
    const cLR = mapViewer.convertCtxPointToLonLat([w, h])
    const cLL = mapViewer.convertCtxPointToLonLat([0, h])
    const coordinates = [cUL, cUR, cLR, cLL, cUL]
    const visibleRegion = polygon([coordinates])
    setVisibleRegionState(visibleRegion)
  } catch {
    /**
     * ignore the error for now
     * Known transient mapbox projection error during terrain updates
     */
  }
}

/**
 * Update the map view zoom level
 * @param {*} map - mapbox instance
 * @param {*} zoom - change in zoom
 */
export const updateZoom = (map, { deltaZoom }) => {
  if (deltaZoom > 0) {
    map.zoomIn()
  } else {
    map.zoomOut()
  }
}

/**
 * *(AI-generated)*
 * Enables or disables the ability for the user to rotate the map.
 */
export const toggleAbilityToRotateMap = (map, turnOn) => {
  if (turnOn) {
    map.dragRotate.enable()
    map.touchZoomRotate.enableRotation()
  } else {
    map.dragRotate.disable()
    map.touchZoomRotate.disableRotation()
  }
}

/**
 * Toggle the ability for the user to change the pitch of the map
 * @param {Object} mapViewer - MapViewer instance
 * @param {Boolean} turnOn - if we want to turn on pitch
 * @param {Boolean} disable3d - if 3d should be disabled for this user
 */
export const toggleAbilityToPitchMap = (
  mapViewer,
  turnOn,
  disable3d = false
) => {
  if (turnOn) {
    // make sure 3d is disabled for non-paying members
    const defaultPitch = disable3d ? 0 : 85
    mapViewer.setMaxPitch(defaultPitch)
    mapViewer.setMinPitch(0)
  } else {
    // otherwise lock to the current pitch value
    const { pitch: currentPitch } = camera()
    const setPitch = currentPitch

    mapViewer.setMaxPitch(setPitch)
    mapViewer.setMinPitch(setPitch)
  }
}
